/**
 * AI Face Try-On — cosmetics, glasses, caps, contact lenses, earrings, etc.
 * Uses Gemini Nano Banana for realistic face + product composition.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Camera as CameraIcon, Upload, Download, X, Loader2, RotateCcw,
  Share2, ChevronRight, Check, RefreshCw, Sparkles,
} from 'lucide-react';
import api from '../../utils/api';

const CHARACTERS = [
  { id: 'male',   label: 'Male',   emoji: '🧑' },
  { id: 'female', label: 'Female', emoji: '👩' },
  { id: 'boy',    label: 'Boy',    emoji: '👦' },
  { id: 'girl',   label: 'Girl',   emoji: '👧' },
];

const LIGHTING = [
  { id: 'studio',       label: 'Studio' },
  { id: 'natural',      label: 'Natural' },
  { id: 'outdoor',      label: 'Outdoor' },
  { id: 'indoor',       label: 'Indoor' },
  { id: 'golden-hour',  label: 'Golden' },
];

const ANGLES = [
  { id: 'front',         label: 'Front' },
  { id: 'three-quarter', label: '3/4' },
  { id: 'side',          label: 'Side' },
];

const useCamera = () => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [active, setActive] = useState(false);
  const start = async (facing = 'user') => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 960 } }, audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      setActive(true);
    } catch (e) { toast.error(`Camera access denied — ${e.message}`); }
  };
  const stop = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setActive(false);
  };
  const capture = () => {
    if (!videoRef.current || !active) return null;
    const v = videoRef.current;
    const c = document.createElement('canvas');
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', 0.92);
  };
  useEffect(() => () => stop(), []); // eslint-disable-line
  return { videoRef, active, start, stop, capture };
};

export default function TrialRoomFaceFlow({ onClose }) {
  const [products, setProducts] = useState([]);
  const [step, setStep] = useState(0); // 0=pick product, 1=customer, 2=product photo, 3=preview
  const [productType, setProductType] = useState('lipstick');
  const [customerImg, setCustomerImg] = useState(null);
  const [productImg, setProductImg] = useState(null);
  const [character, setCharacter] = useState('female');
  const [lighting, setLighting] = useState('studio');
  const [angle, setAngle] = useState('front');
  const [resultImg, setResultImg] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  // 🆕 Feedback state — 👍/👎 with auto-retry-once
  const [lastCacheKey, setLastCacheKey] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [retryAttempted, setRetryAttempted] = useState(false);
  const cam = useCamera();
  const fileRef = useRef(null);

  useEffect(() => { api.get('/trial-room/face-products').then((r) => setProducts(r.data?.products || [])); }, []);

  const capture = () => {
    const d = cam.capture();
    if (!d) return toast.error('Camera not ready');
    cam.stop();
    if (step === 1) { setCustomerImg(d); toast.success('✅ Customer photo captured'); }
    else if (step === 2) { setProductImg(d); toast.success('✅ Product photo captured'); }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error('Photo <8 MB');
    const r = new FileReader();
    r.onload = (ev) => {
      cam.stop();
      if (step === 1) { setCustomerImg(ev.target.result); toast.success('✅ Customer photo loaded'); }
      else if (step === 2) { setProductImg(ev.target.result); toast.success('✅ Product photo loaded'); }
    };
    r.readAsDataURL(f);
    e.target.value = '';
  };

  const run = async (retryBoost = false) => {
    if (!customerImg || !productImg) return;
    setProcessing(true);
    if (!retryBoost) { setFeedbackRating(null); setRetryAttempted(false); }
    try {
      const r = await api.post('/trial-room/face-tryon', {
        customer_image: customerImg,
        product_image: productImg,
        product_type: productType,
        character, lighting, angle, save_history: true,
        retry_boost: !!retryBoost,
      }, { timeout: 90000 });
      if (r.data?.image_base64) {
        setResultImg(`data:${r.data.mime_type};base64,${r.data.image_base64}`);
        setLastCacheKey(r.data.cache_key || null);
        setStep(3);
        toast.success(retryBoost ? '✅ Retry ready! Better result?' : '✅ Face Try-On ready!');
      } else throw new Error('No image returned');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Face try-on failed');
    } finally { setProcessing(false); }
  };

  // 👍/👎 feedback — 👎 auto-retries once with hardened prompt
  const submitFeedback = async (rating) => {
    if (feedbackRating) return;
    setFeedbackRating(rating);
    try {
      await api.post('/trial-room/vton-feedback', {
        rating,
        cache_key: lastCacheKey,
        kind: 'face',
        apparel_type: productType,
        character, lighting, angle,
        was_retry: retryAttempted,
      });
    } catch (_) { /* silent */ }
    if (rating === 'up') {
      toast.success('🙏 Shukriya! Feedback saved.');
    } else {
      toast('👎 Noted. Ek stronger retry chala rahe hain…', { duration: 3000 });
      if (!retryAttempted) {
        setRetryAttempted(true);
        setFeedbackRating(null);
        setTimeout(() => run(true), 400);
      }
    }
  };

  const download = () => {
    const a = document.createElement('a');
    a.href = compareMode ? customerImg : resultImg;
    a.download = `FaceTryOn-${productType}-${Date.now()}.jpg`;
    a.click();
    toast.success('Saved');
  };

  const shareWA = async () => {
    try {
      const resp = await fetch(compareMode ? customerImg : resultImg);
      const blob = await resp.blob();
      const file = new File([blob], `face-tryon-${Date.now()}.jpg`, { type: 'image/jpeg' });
      const text = `Look at my ${products.find((p) => p.id === productType)?.label} try-on! 💄✨ Try yours at Anant Trial Room`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'AI Face Try-On', text });
      } else {
        download();
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
      }
    } catch (e) { toast.error(e.message); }
  };

  const reset = () => {
    setStep(0); setCustomerImg(null); setProductImg(null); setResultImg(null);
    setCompareMode(false); cam.stop();
  };
  const backToCustomer = () => { setStep(1); setCustomerImg(null); cam.stop(); };
  const backToProduct = () => { setStep(2); setProductImg(null); cam.stop(); };

  const selectedProduct = products.find((p) => p.id === productType);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="face-tryon-flow">
      {/* HEADER */}
      <div className="p-3 bg-slate-950 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black">💄 FACE AI TRY-ON</div>
          <h3 className="text-sm md:text-base font-black text-white truncate">Cosmetics · Glasses · Accessories</h3>
        </div>
        <button onClick={onClose} data-testid="face-close" className="p-2 rounded-lg bg-white/10 hover:bg-white/20"><X className="w-4 h-4" /></button>
      </div>

      {/* STEP 0: Product picker */}
      {step === 0 && (
        <div className="flex-1 overflow-auto p-4 bg-slate-950">
          <div className="max-w-3xl mx-auto">
            <h4 className="text-lg font-black text-white text-center mb-3">Kya try karna hai? · What to try?</h4>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {products.map((p) => (
                <button
                  key={p.id}
                  onClick={() => { setProductType(p.id); setStep(1); }}
                  data-testid={`face-product-${p.id}`}
                  className={`rounded-xl p-3 text-left bg-gradient-to-br ${p.gradient} hover:scale-105 transition shadow-lg border-2 border-white/10`}
                >
                  <div className="text-2xl mb-1">{p.label.split(' ')[0]}</div>
                  <div className="text-xs font-black text-white leading-tight">{p.label.replace(/^\S+\s/, '')}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* STEP 1/2: Capture photos */}
      {(step === 1 || step === 2) && (
        <div className="flex-1 flex flex-col items-center justify-center bg-slate-950 overflow-auto p-4">
          <div className="w-full max-w-lg">
            <div className="flex items-center justify-center gap-1 mb-3 flex-wrap">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${step >= 1 ? 'bg-rose-500 text-white' : 'bg-white/10 text-slate-400'}`}>{step > 1 ? '✓' : '1'} Customer</span>
              <ChevronRight className="w-3 h-3 text-slate-500" />
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${step >= 2 ? 'bg-rose-500 text-white' : 'bg-white/10 text-slate-400'}`}>{step > 2 ? '✓' : '2'} {selectedProduct?.label || 'Product'}</span>
              <ChevronRight className="w-3 h-3 text-slate-500" />
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${step >= 3 ? 'bg-rose-500 text-white' : 'bg-white/10 text-slate-400'}`}>3 Preview</span>
            </div>
            <div className="text-center mb-3">
              <div className="text-lg font-black text-white">
                {step === 1 ? '📸 Customer Face Photo' : `${selectedProduct?.label || 'Product'} Photo`}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                {step === 1 ? 'Clear front-facing portrait — good lighting, face visible' : 'Product ka clear photo — Google se ya khud ki'}
              </div>
            </div>
            <div className="rounded-2xl border-2 border-rose-400/40 bg-black/60 overflow-hidden aspect-[3/4] flex items-center justify-center relative">
              {step === 1 && customerImg && <img src={customerImg} alt="Customer" className="w-full h-full object-contain" />}
              {step === 2 && productImg && <img src={productImg} alt="Product" className="w-full h-full object-contain" />}
              {!((step === 1 && customerImg) || (step === 2 && productImg)) && (
                <>
                  <video ref={cam.videoRef} playsInline muted className={`w-full h-full object-cover ${cam.active ? '' : 'opacity-0'}`} />
                  {!cam.active && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
                      <CameraIcon className="w-14 h-14 mb-2 opacity-60" />
                      <div className="text-xs">Camera off</div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {!cam.active && !((step === 1 && customerImg) || (step === 2 && productImg)) && (
                <>
                  <button onClick={() => cam.start(step === 1 ? 'user' : 'environment')} data-testid={`face-start-cam-${step}`} className="px-3 py-2.5 rounded-lg bg-gradient-to-r from-rose-500 to-pink-500 text-white text-xs font-black flex items-center justify-center gap-1.5"><CameraIcon className="w-4 h-4" /> Camera</button>
                  <button onClick={() => fileRef.current?.click()} data-testid={`face-upload-${step}`} className="px-3 py-2.5 rounded-lg bg-white/10 text-white text-xs font-black flex items-center justify-center gap-1.5"><Upload className="w-4 h-4" /> Upload</button>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
                </>
              )}
              {cam.active && (
                <button onClick={capture} data-testid={`face-capture-${step}`} className="col-span-2 px-3 py-3 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-black flex items-center justify-center gap-1.5"><CameraIcon className="w-4 h-4" /> Click Photo</button>
              )}
              {(step === 1 && customerImg) && (
                <>
                  <button onClick={() => setCustomerImg(null)} className="px-3 py-2 rounded-lg bg-white/10 text-white text-xs font-bold"><RefreshCw className="w-3 h-3 inline" /> Retake</button>
                  <button onClick={() => setStep(2)} data-testid="face-next-2" className="px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-black">Next: {selectedProduct?.label} <ChevronRight className="w-3 h-3 inline" /></button>
                </>
              )}
              {(step === 2 && productImg) && (
                <>
                  <button onClick={() => setProductImg(null)} className="px-3 py-2 rounded-lg bg-white/10 text-white text-xs font-bold"><RefreshCw className="w-3 h-3 inline" /> Retake</button>
                  <button onClick={() => run(false)} disabled={processing} data-testid="face-run" className="px-3 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 via-rose-500 to-amber-500 text-white text-xs font-black">{processing ? <><Loader2 className="w-3 h-3 animate-spin inline" /> Processing…</> : <><Sparkles className="w-3 h-3 inline" /> AI Try-On</>}</button>
                </>
              )}
            </div>
            {step === 2 && <button onClick={() => setStep(1)} className="mt-2 text-[11px] text-slate-400 hover:text-white block mx-auto">‹ Back to Customer</button>}
            {step === 1 && <button onClick={() => setStep(0)} className="mt-2 text-[11px] text-slate-400 hover:text-white block mx-auto">‹ Back to Product Category</button>}
          </div>
        </div>
      )}

      {/* STEP 3: Result */}
      {step === 3 && resultImg && (
        <div className="flex-1 flex flex-col overflow-auto">
          <div className="flex-1 flex items-center justify-center bg-slate-950 p-3 relative">
            <img src={compareMode ? customerImg : resultImg} alt="Result" className="max-w-full max-h-full shadow-2xl rounded-xl border border-white/10" data-testid="face-result-img" />
            <button onClick={() => setCompareMode((v) => !v)} className={`absolute top-4 right-4 px-3 py-2 rounded-full text-[11px] font-black flex items-center gap-1 border-2 backdrop-blur transition ${compareMode ? 'bg-amber-500/90 border-amber-300 text-black' : 'bg-black/60 border-white/20 text-white'}`}>
              {compareMode ? 'Original' : 'Compare'}
            </button>
          </div>
          <div className="bg-slate-950 border-t border-white/10 p-3 space-y-2">
            <div className="rounded-lg border border-rose-400/40 bg-rose-500/10 px-2 py-1.5 text-[10px] text-rose-200 text-center font-black">
              💄 REAL AI FACE TRY-ON · Gemini Nano Banana · Product: {selectedProduct?.label}
            </div>
            {/* ⭐ Feedback — 👍/👎 (👎 auto-retries once with hardened prompt) */}
            <div className="flex items-center justify-center gap-2 flex-wrap" data-testid="face-feedback-bar">
              <span className="text-[11px] text-slate-300 font-semibold">Kaisa laga?</span>
              <button
                onClick={() => submitFeedback('up')}
                disabled={!!feedbackRating || processing}
                data-testid="face-feedback-up"
                className={`px-3 py-1.5 rounded-lg text-[11px] font-black flex items-center gap-1.5 border transition disabled:opacity-50 ${feedbackRating === 'up' ? 'bg-emerald-500 border-emerald-300 text-white' : 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20'}`}
              >
                👍 Bahut badhiya
              </button>
              <button
                onClick={() => submitFeedback('down')}
                disabled={!!feedbackRating || processing}
                data-testid="face-feedback-down"
                className={`px-3 py-1.5 rounded-lg text-[11px] font-black flex items-center gap-1.5 border transition disabled:opacity-50 ${feedbackRating === 'down' ? 'bg-rose-500 border-rose-300 text-white' : 'bg-rose-500/10 border-rose-500/40 text-rose-300 hover:bg-rose-500/20'}`}
              >
                👎 {retryAttempted ? 'Phir bhi kharab' : 'Sahi nahi'}
              </button>
              {retryAttempted && <span className="text-[10px] text-amber-300 font-semibold">🔄 Retry used</span>}
            </div>
            {/* Lighting + Angle tuners (regenerate on change) */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[9px] uppercase tracking-widest text-slate-400 font-black mb-1">Lighting</div>
                <div className="flex gap-1 flex-wrap">
                  {LIGHTING.map((l) => (
                    <button key={l.id} onClick={() => setLighting(l.id)} className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${lighting === l.id ? 'bg-amber-500 text-black' : 'bg-white/5 text-slate-300 border border-white/10'}`}>{l.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-slate-400 font-black mb-1">Angle</div>
                <div className="flex gap-1 flex-wrap">
                  {ANGLES.map((a) => (
                    <button key={a.id} onClick={() => setAngle(a.id)} className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${angle === a.id ? 'bg-cyan-500 text-black' : 'bg-white/5 text-slate-300 border border-white/10'}`}>{a.label}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap justify-center">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black">Character:</div>
              {CHARACTERS.map((c) => (
                <button key={c.id} onClick={() => setCharacter(c.id)} className={`px-2.5 py-1 rounded-full text-[11px] font-black flex items-center gap-1 border ${character === c.id ? 'bg-fuchsia-500 border-fuchsia-400 text-white' : 'bg-white/5 border-white/10 text-slate-300'}`}>
                  {c.emoji} {c.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 justify-center pt-1">
              <button onClick={() => run(false)} disabled={processing} data-testid="face-regenerate" className="px-3 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white text-xs font-black flex items-center gap-1 disabled:opacity-50">
                {processing ? <><Loader2 className="w-3 h-3 animate-spin" /> Processing</> : <><Sparkles className="w-3 h-3" /> Regenerate</>}
              </button>
              <button onClick={download} data-testid="face-download" className="px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-black flex items-center gap-1"><Download className="w-3 h-3" /> Download</button>
              <button onClick={shareWA} data-testid="face-share" className="px-3 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-black flex items-center gap-1"><Share2 className="w-3 h-3" /> WhatsApp</button>
              <button onClick={backToProduct} className="px-3 py-2 rounded-lg bg-white/10 text-white text-xs font-bold flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Change Product Photo</button>
              <button onClick={backToCustomer} className="px-3 py-2 rounded-lg bg-white/10 text-white text-xs font-bold flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Change Customer</button>
              <button onClick={reset} data-testid="face-reset" className="px-3 py-2 rounded-lg bg-white/10 text-white text-xs font-bold flex items-center gap-1"><RotateCcw className="w-3 h-3" /> New Try-On</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
